/**
 * Audit automatisé des générateurs d'exercices.
 *
 * Usage : depuis la console navigateur, après import :
 *   auditGen(596)       → audit le gen_596 (20 itérations par format)
 *   auditGen(596, 50)   → audit avec 50 itérations
 *   auditAllGens()      → audit TOUS les gens enregistrés
 *
 * Vérifie :
 * 1. Unicité : sur N itérations, l'exercice (énoncé + réponses triées) doit être différent
 * 2. Pas de propositions dupliquées dans un même exercice (V/F, QCM)
 * 3. Bonne réponse correcte (présente dans les choix pour QCM, cohérente pour V/F)
 * 4. Nombre minimum de propositions (V/F >= 3, QCM >= 3 choix)
 * 5. Pas de valeurs aberrantes (NaN, undefined, null dans les textes)
 */

import { generateExercice, getCategories, hasGenerator } from '../index';

/** Construit une empreinte unique d'un exercice (ordre des propositions ignoré) */
const fingerprint = (payload) => {
    const texte = (payload.texte || '').replace(/<[^>]*>/g, '').trim();
    const contenu = payload.contenu || {};

    let parts = [texte];

    if (contenu.choix && Array.isArray(contenu.choix)) {
        parts.push('choix:' + [...contenu.choix].sort().join('|'));
    }
    if (contenu.bonne_reponse != null) {
        const br = typeof contenu.bonne_reponse === 'string'
            ? contenu.bonne_reponse
            : JSON.stringify(contenu.bonne_reponse);
        parts.push('br:' + br);
    }
    if (contenu.phrase) parts.push('phrase:' + contenu.phrase);
    if (contenu.pairs) parts.push('pairs:' + JSON.stringify([...contenu.pairs].sort((a, b) => a.source?.localeCompare(b.source))));
    // Cibles leurres : alignement avec l'empreinte v2 (qaGen.js) — mêmes paires +
    // leurres différents = variante réelle
    if (Array.isArray(contenu.cibles_leurres) && contenu.cibles_leurres.length > 0) {
        parts.push('leurres:' + [...contenu.cibles_leurres].map(l => l.target).sort().join('|'));
    }
    if (contenu.categories) parts.push('cats:' + JSON.stringify(contenu.categories));
    if (contenu.items) parts.push('items:' + [...contenu.items].sort().join('|'));

    // programme_construction : inclure tasks + points dans le fingerprint
    if (contenu.subType === 'programme_construction') {
        const taskSig = (contenu.tasks || []).map(t => `${t.id}:${t.consigne}`).join('|');
        const ptSig = (contenu.points || []).map(p => `${p.name}@${p.x},${p.y}`).join(',');
        parts.push('pc:' + taskSig);
        parts.push('pts:' + ptSig);
    }

    // placer_aiguilles : la solution semantique dans le fingerprint
    if (contenu.subType === 'placer_aiguilles') parts.push('aig:' + contenu.bonne_reponse);

    // V/F : extraire les lignes du tableau
    const els = payload.elements_additionnels?.elements || [];
    const tableEl = els.find(e => e.type === 'table' && e.config?.type === 'vraiFaux');
    if (tableEl) {
        const brSorted = (tableEl.config.lignes || []).map((l, i) => `${l}:${tableEl.config.bonnesReponses[i]}`).sort();
        parts.push('vf:' + brSorted.join('|'));
    }

    // Autres elements_additionnels (expression, numberline, image, geogebra...)
    // → intégrés au fingerprint pour éviter les "faux doublons" quand seule la
    //    figure visible varie (KaTeX, SVG, droite graduée).
    els.forEach(el => {
        if (!el || !el.type) return;
        if (el.type === 'expression') {
            parts.push('expr:' + (el.config?.contenu || ''));
        } else if (el.type === 'numberline') {
            const cfg = el.config || {};
            const pts = (cfg.points || []).map(p => `${p.value}@${p.label || ''}`).join(',');
            const reperes = (cfg.reperes || []).join(',');
            parts.push(`nl:${cfg.start}-${cfg.end}-${cfg.step}[${pts}](${reperes})`);
        } else if (el.type === 'image' || el.type === 'svgImage') {
            // Hash du contenu COMPLET (djb2) — même logique que qaGen.js (les
            // deux empreintes doivent rester synchrones) : la troncature à 80
            // caractères confondait toutes les images d'un même gen.
            const data = String(el.config?.imageData || el.config?.svg || el.config?.imageUrl || '');
            let h = 5381;
            for (let i = 0; i < data.length; i++) h = ((h << 5) + h + data.charCodeAt(i)) >>> 0;
            parts.push('img:' + data.length + ':' + h.toString(36));
        } else if (el.type === 'geogebra') {
            parts.push('ggb:' + (el.config?.ggb_base64 || '').slice(0, 60));
        } else if (el.type === 'table' && el.config?.type !== 'vraiFaux') {
            parts.push('tbl:' + JSON.stringify(el.config).slice(0, 120));
        }
    });

    return parts.join('§');
};

/** Vérifie un seul exercice généré */
const checkExercice = (payload, formatKey) => {
    const errors = [];
    const texte = payload.texte || '';
    const contenu = payload.contenu || {};

    // Check valeurs aberrantes dans le texte
    if (texte.includes('undefined') || texte.includes('NaN') || texte.includes('null')) {
        errors.push(`Valeur aberrante dans l'énoncé : "${texte.slice(0, 100)}"`);
    }

    // Check V/F spécifique
    const els = payload.elements_additionnels?.elements || [];
    const tableEl = els.find(e => e.type === 'table' && e.config?.type === 'vraiFaux');
    if (tableEl) {
        const lignes = tableEl.config.lignes || [];
        const bonnes = tableEl.config.bonnesReponses || [];

        // Propositions dupliquées
        if (new Set(lignes).size !== lignes.length) {
            const dupes = lignes.filter((l, i) => lignes.indexOf(l) !== i);
            errors.push(`V/F propositions dupliquées : ${JSON.stringify(dupes)}`);
        }

        // Minimum 3 propositions
        if (lignes.length < 3) {
            errors.push(`V/F seulement ${lignes.length} propositions (min 3)`);
        }

        // Bonnes réponses cohérentes (0 = Vrai, 1 = Faux)
        if (bonnes.some(b => b !== 0 && b !== 1)) {
            errors.push(`V/F bonnes réponses invalides : ${JSON.stringify(bonnes)}`);
        }

        // Note : on n'exige PAS de mix V/F — pédagogiquement acceptable d'avoir
        // 100% V ou 100% F si le générateur le décide.

        // Valeurs aberrantes dans les propositions
        lignes.forEach((l, i) => {
            if (l.includes('undefined') || l.includes('NaN') || l.includes('null')) {
                errors.push(`V/F proposition ${i} aberrante : "${l}"`);
            }
        });
    }

    // Check QCM
    if (contenu.choix && Array.isArray(contenu.choix) && contenu.choix.length > 0) {
        // Minimum 3 choix
        if (contenu.choix.length < 3) {
            errors.push(`QCM seulement ${contenu.choix.length} choix (min 3)`);
        }

        // Choix dupliqués
        const choixStrings = contenu.choix.map(c => typeof c === 'string' ? c : JSON.stringify(c));
        if (new Set(choixStrings).size !== choixStrings.length) {
            const dupes = choixStrings.filter((c, i) => choixStrings.indexOf(c) !== i);
            errors.push(`QCM choix dupliqués : ${JSON.stringify(dupes)}`);
        }

        // Bonne réponse présente — format lettre (A/B/C/D) ou valeur textuelle
        if (contenu.bonne_reponse != null) {
            const br = contenu.bonne_reponse;
            const LETTRES_MAP = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7 };

            // QCM simple : bonne_reponse est une string (lettre OU valeur textuelle)
            if (typeof br === 'string' && !contenu.multi) {
                // Format V/F encodé : skip
                if (br.startsWith('[')) {
                    // OK, format V/F
                } else if (br.length === 1 && LETTRES_MAP[br] !== undefined) {
                    // Format lettre : vérifier que l'index existe dans les choix
                    const idx = LETTRES_MAP[br];
                    if (idx >= contenu.choix.length) {
                        errors.push(`QCM lettre "${br}" (index ${idx}) hors des ${contenu.choix.length} choix`);
                    }
                } else if (!choixStrings.includes(br)) {
                    errors.push(`QCM bonne réponse "${br}" absente des choix`);
                }
            }
            // QCM multi : bonne_reponse est un array de lettres
            else if (Array.isArray(br) && contenu.multi) {
                br.forEach(lettre => {
                    if (LETTRES_MAP[lettre] === undefined || LETTRES_MAP[lettre] >= contenu.choix.length) {
                        errors.push(`QCM Multi lettre invalide : "${lettre}"`);
                    }
                });
            }
        }

        // Valeurs aberrantes
        choixStrings.forEach((c, i) => {
            if (c.includes('undefined') || c.includes('NaN')) {
                errors.push(`QCM choix ${i} aberrant : "${c}"`);
            }
        });
    }

    // Check Saisie
    if (contenu.bonne_reponse != null && !contenu.choix && !tableEl) {
        const br = String(contenu.bonne_reponse);
        if (br === 'undefined' || br === 'NaN' || br === 'null' || br === '') {
            errors.push(`Saisie bonne réponse invalide : "${br}"`);
        }
    }

    // Check Texte à trous
    if (contenu.phrase) {
        if (contenu.phrase.includes('undefined') || contenu.phrase.includes('NaN')) {
            errors.push(`Texte à trous phrase aberrante`);
        }
        // Vérifier qu'il y a au moins un trou
        if (!contenu.phrase.includes('{{')) {
            errors.push(`Texte à trous sans trou : "${contenu.phrase.slice(0, 80)}"`);
        }
    }

    return errors;
};

/** Audit un attendu complet */
export const auditGen = (attenduId, iterations = 20) => {
    if (!hasGenerator(attenduId)) {
        console.error(`❌ Pas de générateur pour l'attendu ${attenduId}`);
        return;
    }

    const categories = getCategories(attenduId);
    console.log(`\n🔍 AUDIT gen_${attenduId} — ${categories.length} catégorie(s), ${iterations} itérations/format\n`);

    let totalFormats = 0;
    let totalOk = 0;
    let totalWarnings = 0;
    let totalErrors = 0;

    categories.forEach(cat => {
        cat.formatsList.forEach(fmt => {
            totalFormats++;
            const fingerprints = new Set();
            const errors = [];
            let duplicateCount = 0;

            for (let i = 0; i < iterations; i++) {
                try {
                    const payload = generateExercice(attenduId, fmt.key);

                    // Check unicité
                    const fp = fingerprint(payload);
                    if (fingerprints.has(fp)) {
                        duplicateCount++;
                    }
                    fingerprints.add(fp);

                    // Check erreurs pédagogiques
                    const exErrors = checkExercice(payload, fmt.key);
                    if (exErrors.length > 0) {
                        errors.push({ iteration: i + 1, errors: exErrors });
                    }
                } catch (err) {
                    errors.push({ iteration: i + 1, errors: [`CRASH: ${err.message}`] });
                }
            }

            const uniqueCount = fingerprints.size;
            const hasErrors = errors.length > 0;
            const hasDupes = duplicateCount > 0;

            if (!hasErrors && !hasDupes) {
                console.log(`  ✅ ${fmt.key} : ${uniqueCount}/${iterations} uniques, 0 erreurs`);
                totalOk++;
            } else if (hasDupes && !hasErrors) {
                console.warn(`  ⚠️ ${fmt.key} : ${uniqueCount}/${iterations} uniques (${duplicateCount} doublons)`);
                totalWarnings++;
            } else {
                console.error(`  ❌ ${fmt.key} : ${uniqueCount}/${iterations} uniques, ${errors.length} erreurs`);
                errors.slice(0, 3).forEach(e => {
                    console.error(`     → itération ${e.iteration}: ${e.errors.join(', ')}`);
                });
                if (errors.length > 3) console.error(`     ... et ${errors.length - 3} autres erreurs`);
                totalErrors++;
            }
        });
    });

    console.log(`\n📊 BILAN gen_${attenduId} : ${totalOk} ✅  ${totalWarnings} ⚠️  ${totalErrors} ❌  (${totalFormats} formats testés)\n`);

    return { attenduId, totalFormats, totalOk, totalWarnings, totalErrors };
};

/** Audit TOUS les gens enregistrés */
export const auditAllGens = (iterations = 20) => {
    // IDs dérivés du registry (plus jamais de liste hardcodée : l'ancienne
    // s'arrêtait à 652 et laissait 653-686 sans audit)
    // eslint-disable-next-line global-require
    const allIds = Object.keys(require('../index').default).map(Number).sort((a, b) => a - b);

    console.log(`\n${'═'.repeat(60)}`);
    console.log(`  AUDIT COMPLET — ${allIds.length} générateurs × ${iterations} itérations`);
    console.log(`${'═'.repeat(60)}\n`);

    const results = [];

    allIds.forEach(id => {
        if (hasGenerator(id)) {
            results.push(auditGen(id, iterations));
        }
    });

    const totals = results.reduce((acc, r) => ({
        formats: acc.formats + r.totalFormats,
        ok: acc.ok + r.totalOk,
        warnings: acc.warnings + r.totalWarnings,
        errors: acc.errors + r.totalErrors,
    }), { formats: 0, ok: 0, warnings: 0, errors: 0 });

    console.log(`\n${'═'.repeat(60)}`);
    console.log(`  TOTAL : ${totals.ok} ✅  ${totals.warnings} ⚠️  ${totals.errors} ❌  (${totals.formats} formats)`);
    console.log(`${'═'.repeat(60)}\n`);

    return totals;
};

// Exposer sur window pour usage console navigateur
if (typeof window !== 'undefined') {
    window.auditGen = auditGen;
    window.auditAllGens = auditAllGens;
}
